import { saveBrowserVaultEntry } from '@/lib/browser-vault';
import { getActiveToolShareContext, dispatchSharePromptForActiveTool } from '@/lib/tool-share-prompt';
import { getToolById } from '@/lib/tools/tool-registry';

function isIOSDevice() {
  return (
    typeof navigator !== 'undefined' &&
    (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
  );
}

function canSaveToBrowserVault() {
  return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';
}

function inferOutputFormat(fileName: string, blobType?: string) {
  const extension = fileName.split('.').pop()?.trim();
  if (extension) {
    return extension.toUpperCase();
  }

  if (blobType?.includes('/')) {
    return blobType.split('/').pop()?.toUpperCase() || 'FILE';
  }

  return 'FILE';
}

async function saveDownloadToBrowserVault(blob: Blob, fileName: string) {
  if (!canSaveToBrowserVault()) {
    return;
  }

  const activeTool = getActiveToolShareContext();
  if (!activeTool || activeTool.toolId === 'invoice-maker') {
    return;
  }

  const tool = getToolById(activeTool.toolId);
  if (!tool) {
    return;
  }

  try {
    await saveBrowserVaultEntry({
      toolId: tool.id,
      toolName: tool.name,
      toolCategory: tool.category,
      inputLabel: `${tool.name} output`,
      outputFormat: inferOutputFormat(fileName, blob.type),
      fileName,
      thumbnailColor: '#1373dc',
      blob,
      draft: null,
    });
  } catch (error) {
    console.error('Failed to save browser download to the local vault:', error);
  }
}

export function triggerFileDownload(url: string, fileName: string) {
  const isIOS = isIOSDevice();
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener noreferrer';

  // iOS Safari can ignore the download attribute for blob/data URLs.
  // Opening the file in a new tab gives users a reliable system share/save path.
  if (isIOS) {
    link.target = '_blank';
  }

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  dispatchSharePromptForActiveTool(fileName.split('.').pop()?.toUpperCase() || 'FILE');

  if (url.startsWith('blob:') || url.startsWith('data:')) {
    window.setTimeout(() => {
      void fetch(url)
        .then((response) => response.blob())
        .then((blob) => saveDownloadToBrowserVault(blob, fileName))
        .catch(() => {
          // Ignore browser vault save failures.
        });
    }, 0);
  }
}

export function downloadBlobFile(blob: Blob, fileName: string) {
  void saveDownloadToBrowserVault(blob, fileName);

  if (isIOSDevice() && typeof navigator !== 'undefined' && 'share' in navigator) {
    const file = new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
    const canShare = 'canShare' in navigator ? navigator.canShare?.({ files: [file] }) : false;

    if (canShare) {
      void navigator.share({ files: [file], title: fileName }).catch(() => {});
      return;
    }
  }

  const url = URL.createObjectURL(blob);
  triggerFileDownload(url, fileName);
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
